import { createColumnHelper } from '@tanstack/react-table';
import { isNumber } from 'fp-ts/number';
import { OnboardingAuditField, OnboardingAuditStatus } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedEnum, lt } from '@mews/framework';
import { Body, Container, Stack, Tag, Typography } from '@optimus-web/core';
import { ChartData, ProgressChart } from '@mews/b2b-ui';
import { RowActionCell } from '../../overrideOnboardingAudit/RowActionCell';
import { calculateAggregateStatus } from '../../utils/auditTransforms';
import { AuditName } from '../../../components/AuditName';
import { SectionStatusTag, tagVariantHandler, TaskStatusTag } from '../../../components/AuditStatusTag';
import { AuditNameLink } from './AuditNameLink';
import { MarkdownNotes } from './MarkdownNotes';

const columnHelper = createColumnHelper<OnboardingAuditField>();

export const getColumns = (inDialog?: boolean) => {
    const columns = [
        columnHelper.accessor('area', {
            aggregatedCell: (props) => {
                return (
                    <Typography textStyle="bodyMediumStrong" as="div">
                        <LocalizedEnum prefix="OnboardingAuditArea" value={props.row.original.area} />
                    </Typography>
                );
            },
        }),
        columnHelper.accessor('fieldIdentifier', {
            header: () => lt('GoLiveAuditsTableColumnTask'),
            cell: ({ row: { original }, getValue }) => {
                if (inDialog) {
                    return <AuditName fieldIdentifier={original.fieldIdentifier} />;
                }

                return <AuditNameLink fieldIdentifier={getValue()} />;
            },
            meta: {
                align: 'start',
                width: 350,
            },
        }),
        columnHelper.accessor('status', {
            header: () => lt('GoLiveAuditsTableColumnStatus'),
            cell: ({ row: { original } }) => {
                const hasCount = isNumber(original.count);
                const hasTotal = isNumber(original.totalCount);

                if (!hasCount) {
                    return <TaskStatusTag status={original.status} />;
                }

                return (
                    <Stack spacing="50">
                        <Tag variant={tagVariantHandler(original.status)}>
                            {hasTotal
                                ? `${original.count} ${UnicodeEntity.ForwardSlash} ${original.totalCount}`
                                : original.count}
                        </Tag>
                        <TaskStatusTag status={original.status} />
                    </Stack>
                );
            },
            aggregationFn: (columnId, _leafRows, childRows) => {
                const statuses = childRows.map((row) => row.getValue(columnId)) satisfies OnboardingAuditStatus[];
                return calculateAggregateStatus(statuses);
            },
            aggregatedCell: (props) => <SectionStatusTag status={props.getValue()} />,

            meta: {
                align: 'start',
                width: 250,
            },
        }),
        columnHelper.accessor('notes', {
            header: () => lt('GoLiveAuditsTableColumnObservations'),
            cell: ({ getValue, row }) => (
                <Stack spacing="100" vertical>
                    <MarkdownNotes observations={getValue()} />
                    {row.original.onboardingManagerNotes && (
                        <Stack vertical>
                            <Typography textStyle="bodyMediumStrong" as="div">
                                {lt('GoLiveAuditsTableColumnNotes')}
                            </Typography>
                            <Body size="small">{row.original.onboardingManagerNotes}</Body>
                        </Stack>
                    )}
                </Stack>
            ),
            meta: {
                align: 'start',
            },
        }),
    ];

    if (!inDialog) {
        columns.push(
            columnHelper.display({
                id: 'actions',
                meta: { align: 'end' },
                cell: ({ row: { original } }) => <RowActionCell fieldIdentifier={original.fieldIdentifier} />,
                aggregationFn: (_columnId, _leafRows, childRows) =>
                    childRows.filter((row) =>
                        [OnboardingAuditStatus.Completed, OnboardingAuditStatus.NotApplicable].includes(
                            row.original.status
                        )
                    ).length,
                aggregatedCell: (props) => {
                    const progress = props.getValue() as number;
                    const total = props.row.subRows.length;
                    const areaProgressData: ChartData[] = [
                        {
                            id: props.row.groupingValue?.toString() || '',
                            name: null,
                            value: progress,
                            color: 'blue',
                        },
                    ];

                    return (
                        <Stack spacing="50" distribution="space-between" itemAlignment="center">
                            <ProgressChart minWidth="75px" total={total} data={areaProgressData} />
                            <Container width="50px">
                                <Stack alignment="end">
                                    {progress} {UnicodeEntity.ForwardSlash} {total}
                                </Stack>
                            </Container>
                        </Stack>
                    );
                },
            })
        );
    }

    return columns;
};
